/**
 * Campo CUIT con formateo automático y verificación de unicidad en tiempo real
 * Formato: XX-XXXXXXXX-X
 */
import { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { CheckCircle2, XCircle, Loader2, AlertCircle, Search } from 'lucide-react'
import { cn, validarCUIT } from '@/lib/utils'
import { useVerificarCUIT } from '@/hooks/useVerificarCUIT'
import { getFieldError, type ClienteForm } from '../useClienteForm'
import type { PersonaAFIP } from '@/types/afip'

interface FormCUITProps {
  form: ClienteForm
  name: 'cuit' | 'admin_cuit'
  label: string
  required?: boolean
  placeholder?: string
  /** ID del cliente actual (para excluir en verificación de unicidad en modo edición) */
  excluirClienteId?: string
  /** Si es true, verifica unicidad en la organización */
  verificarUnicidad?: boolean
  /** Callback para consultar ARCA con el CUIT */
  onConsultarARCA?: (cuit: string) => void
  /** Si está consultando ARCA */
  isConsultandoARCA?: boolean
  /** Datos obtenidos de ARCA */
  datosARCA?: PersonaAFIP | null
}

/**
 * Formatea un CUIT mientras se escribe
 * XX-XXXXXXXX-X
 */
function formatCuitOnType(value: string): string {
  const raw = value.replace(/\D/g, '')
  const limited = raw.slice(0, 11)

  if (limited.length <= 2) {
    return limited
  }
  if (limited.length <= 10) {
    return `${limited.slice(0, 2)}-${limited.slice(2)}`
  }
  return `${limited.slice(0, 2)}-${limited.slice(2, 10)}-${limited.slice(10)}`
}

export function FormCUIT({
  form,
  name,
  label,
  required = false,
  placeholder = '20-12345678-9',
  excluirClienteId,
  verificarUnicidad = true,
  onConsultarARCA,
  isConsultandoARCA = false,
  datosARCA,
}: FormCUITProps) {
  // Estado local para rastrear el valor actual para el hook
  const [currentValue, setCurrentValue] = useState('')

  // Sincronizar con el form state
  useEffect(() => {
    const formValue = form.state.values[name] || ''
    if (formValue !== currentValue) {
      setCurrentValue(formValue)
    }
    // Solo form.state.values[name] y name son dependencias
    // currentValue NO debe estar aquí porque lo modificamos dentro del effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.state.values[name], name])

  const cuitLimpio = currentValue.replace(/\D/g, '')
  const tiene11Digitos = cuitLimpio.length === 11

  // Validación de formato (nivel superior)
  const validation = tiene11Digitos ? validarCUIT(currentValue) : null
  const formatoValido = validation?.valido

  // Hook de verificación de unicidad (nivel superior del componente)
  const debeVerificarUnicidad = verificarUnicidad && name === 'cuit' && formatoValido
  const {
    data: verificacion,
    isLoading: verificandoUnicidad,
  } = useVerificarCUIT(
    debeVerificarUnicidad ? currentValue : null,
    excluirClienteId
  )

  return (
    <form.Field name={name}>
      {(field) => {
        const value = field.state.value || ''
        const hasError = field.state.meta.errors.length > 0

        // Estado final
        const esDuplicado = debeVerificarUnicidad && verificacion && !verificacion.disponible
        const isValid = formatoValido && !hasError && !esDuplicado && !verificandoUnicidad

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const formatted = formatCuitOnType(e.target.value)
          field.handleChange(formatted)
          setCurrentValue(formatted)
        }

        // Determinar mensaje a mostrar
        let mensaje = '\u00A0'
        let mensajeClase = 'text-transparent'

        if (hasError) {
          mensaje = getFieldError(field.state.meta.errors) || 'Error'
          mensajeClase = 'text-destructive'
        } else if (esDuplicado) {
          mensaje = `CUIT ya registrado: ${verificacion.clienteExistente?.nombre}`
          mensajeClase = 'text-destructive'
        } else if (verificandoUnicidad && formatoValido) {
          mensaje = 'Verificando disponibilidad...'
          mensajeClase = 'text-muted-foreground'
        } else if (isValid) {
          mensaje = `CUIT válido: ${validation?.cuitFormateado}`
          mensajeClase = 'text-green-600'
        } else if (tiene11Digitos && !formatoValido) {
          mensaje = 'CUIT inválido'
          mensajeClase = 'text-destructive'
        }

        return (
          <div className="space-y-1">
            <Label htmlFor={name} className="text-base font-semibold">
              {label} {required && '*'}
            </Label>
            <div className="relative">
              <Input
                id={name}
                placeholder={placeholder}
                value={value}
                onChange={handleChange}
                onBlur={field.handleBlur}
                maxLength={13}
                className={cn(
                  'h-11 pr-10',
                  (hasError || esDuplicado) && 'border-destructive focus-visible:ring-destructive',
                  isValid && 'border-green-500 focus-visible:ring-green-500'
                )}
              />
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                {verificandoUnicidad && formatoValido ? (
                  <Loader2 className="h-5 w-5 text-muted-foreground animate-spin" />
                ) : esDuplicado ? (
                  <AlertCircle className="h-5 w-5 text-destructive" />
                ) : isValid ? (
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                ) : (tiene11Digitos || hasError) ? (
                  <XCircle className="h-5 w-5 text-destructive" />
                ) : null}
              </div>
            </div>
            <p className={cn('text-sm min-h-5', mensajeClase)}>
              {mensaje}
            </p>

            {/* Botón Consultar ARCA */}
            {isValid && name === 'cuit' && onConsultarARCA && (
              <div className="space-y-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isConsultandoARCA}
                  onClick={() => onConsultarARCA(value)}
                  className="w-full"
                >
                  {isConsultandoARCA ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Search className="h-4 w-4 mr-2" />
                  )}
                  {isConsultandoARCA ? 'Consultando ARCA...' : 'Consultar ARCA'}
                </Button>

                {/* Badge con datos obtenidos */}
                {datosARCA && (
                  <div className="flex items-start gap-2 p-2.5 bg-emerald-50 border border-emerald-200 rounded-md dark:bg-emerald-950/30 dark:border-emerald-800">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0 dark:text-emerald-400" />
                    <div className="text-xs space-y-0.5">
                      <p className="font-medium text-emerald-800 dark:text-emerald-300">
                        {datosARCA.nombre
                          ? `${datosARCA.nombre} ${datosARCA.apellido || ''}`.trim()
                          : datosARCA.razonSocial || 'Sin nombre'}
                      </p>
                      <p className="text-emerald-700 dark:text-emerald-400">
                        {datosARCA.condicionIVA.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                        {datosARCA.categoriaMonotributo && ` - Cat. ${datosARCA.categoriaMonotributo}`}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )
      }}
    </form.Field>
  )
}
